
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { LineItemFormValues, MarkupTier } from './useLineItemFormSchema';
import { PricingTier } from '../types';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';

interface CategoryOrTrade {
  id: string;
  name: string;
  company_id: string;
  created_at?: string;
}

interface UseLineItemDataProps {
  lineItemId?: string;
  form: ReturnType<typeof useForm<LineItemFormValues>>;
  setEnableConversion: (enableConversion: boolean) => void;
  setEnableProductivity: (enableProductivity: boolean) => void;
  setImagePreviewUrls: (imagePreviewUrls: string[]) => void;
  setPricingTiers: (pricingTiers: PricingTier[]) => void;
  setSelectedCategory: (category: CategoryOrTrade | null) => void;
  setSelectedTrade: (trade: CategoryOrTrade | null) => void;
  setSelectedBrowseCategory?: (id: string | null) => void;
}

export const useLineItemData = ({
  lineItemId,
  form,
  setEnableConversion,
  setEnableProductivity,
  setImagePreviewUrls,
  setPricingTiers,
  setSelectedCategory,
  setSelectedTrade,
  setSelectedBrowseCategory
}: UseLineItemDataProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const isEditMode = !!lineItemId;
  
  // Load data if editing an existing line item
  useEffect(() => {
    if (!lineItemId) {
      setIsLoading(false);
      return;
    }
    
    const fetchLineItemData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch line item details
        const { data: lineItemData, error } = await supabase
          .from('internal_line_items')
          .select('*')
          .eq('id', lineItemId)
          .single();
          
        if (error) throw error;
        
        // Handle base unit
        const baseUnit = lineItemData.base_unit || 'hr';
        
        // Handle base cost
        const baseCost = lineItemData.base_cost || 0;
        
        // Fetch pricing tiers
        const { data: pricingTiersData, error: pricingTiersError } = await supabase
          .from('internal_pricing_tiers')
          .select('*')
          .eq('line_item_id', lineItemId)
          .order('qty_min', { ascending: true });
          
        if (pricingTiersError) throw pricingTiersError;
        
        // Fetch custom markup tiers
        const { data: markupTiersData, error: markupTiersError } = await supabase
          .from('line_item_markup_tiers')
          .select()
          .eq('line_item_id', lineItemId)
          .order('min_amount', { ascending: true });
        
        // Handle the error properly - only throw if it's not a table not found error
        if (markupTiersError && !markupTiersError.message.includes('relation "line_item_markup_tiers" does not exist')) {
          throw markupTiersError;
        }
        
        // Set categories and trades based on the line item's fields
        let categoryData = null;
        let tradeData = null;
        
        // Get all categories first
        const { data: allCategories } = await supabase
          .rpc('get_all_categories', { user_company_id: lineItemData.company_id });
          
        // Get all trades  
        const { data: allTrades } = await supabase
          .rpc('get_all_trades', { user_company_id: lineItemData.company_id });
        
        if (lineItemData.category && allCategories) {
          categoryData = allCategories.find(cat => cat.name === lineItemData.category);
        }
        
        if (lineItemData.trade && allTrades) {
          tradeData = allTrades.find(trade => trade.name === lineItemData.trade);
        }

        // Determine if we have custom markup tiers and if they should be used
        const hasCustomTiers = markupTiersData && markupTiersData.length > 0;
        
        // Safely access the new fields with proper fallbacks
        const overrideMarkup = lineItemData.override_markup ?? false;
        const customMarkup = lineItemData.custom_markup ?? 0;
        const useCustomTiers = lineItemData.use_custom_tiers ?? hasCustomTiers;
        
        // Ensure markup_source is one of the expected values
        const markupSource = (['custom', 'category', 'global'] as const).includes(lineItemData.markup_source as any) 
          ? lineItemData.markup_source as 'custom' | 'category' | 'global'
          : 'global';
        
        // Set form values with PROPER custom markup loading
        form.reset({
          title: lineItemData.title || '',
          description: lineItemData.description || '',
          base_unit: baseUnit,
          base_cost: baseCost,
          conversion_unit: lineItemData.conversion_unit || '',
          conversion_factor: lineItemData.conversion_factor || 0,
          productivity_unit: lineItemData.productivity_unit || '',
          productivity_rate: lineItemData.productivity_rate || 0,
          tags: lineItemData.tags || [],
          is_active: lineItemData.is_active,
          image_urls: lineItemData.image_urls || [],
          category: lineItemData.category || null,
          trade: lineItemData.trade || null,
          // PROPERLY load custom markup settings from database
          override_markup: overrideMarkup,
          custom_markup: customMarkup,
          custom_margin: 0, // Calculate this if needed
          category_markup: 0,
          category_margin: 0,
          // Set custom markup tiers
          custom_tiers: markupTiersData || [],
          use_custom_tiers: useCustomTiers,
          // Make sure category_id is set properly
          category_id: categoryData?.id || null,
          // Set pricing source based on stored data
          pricing_source: markupSource
        });
        
        // Update visibility of conversion/productivity fields
        setEnableConversion(!!lineItemData.conversion_unit && !!lineItemData.conversion_factor);
        setEnableProductivity(!!lineItemData.productivity_unit && !!lineItemData.productivity_rate);
        
        // Update image preview URLs
        if (lineItemData.image_urls && lineItemData.image_urls.length > 0) {
          setImagePreviewUrls(lineItemData.image_urls);
        } else {
          setImagePreviewUrls([]);
        }
        
        // Update pricing tiers
        if (pricingTiersData && pricingTiersData.length > 0) {
          setPricingTiers(pricingTiersData);
        } else {
          setPricingTiers([]);
        }
        
        // Set selected category and trade
        if (categoryData) {
          setSelectedCategory(categoryData);
        }
        
        if (tradeData) {
          setSelectedTrade(tradeData);
        }

        if (setSelectedBrowseCategory) {
          setSelectedBrowseCategory((lineItemData as any).browse_category_id ?? null);
        }

      } catch (error) {
        console.error("Error loading line item data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLineItemData();
  }, [lineItemId, form, setEnableConversion, setEnableProductivity, setImagePreviewUrls, setPricingTiers, setSelectedCategory, setSelectedTrade]);
  
  // Load available categories and trades
  useEffect(() => {
    if (!isEditMode) {
      setIsLoading(false);
    }
  }, [isEditMode]);
  
  return {
    isLoading,
    isEditMode
  };
};

// Export types
export type { CategoryOrTrade };
